import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Placeholder,
} from "reactstrap";

import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import SupportAttachmentList from "../../../../Components/Support/SupportAttachmentList";
import { copyTextToClipboard } from "../../../../helpers/clipboard";
import {
  buildRecurringThemeBacklogMarkdown,
  buildRecurringThemeGithubIssueUrl,
  clearCustomSupportSnippet,
  copySupportTicketLink,
  formatRelativeUntil,
  formatSlaLabel,
  loadCustomSupportSnippets,
  playSoftNotificationBeep,
  resolveAgentSla,
  resolveCurrentSuperAdminId,
  resolveSupportLivePollMs,
  resolveSupportSnippetBody,
  saveCustomSupportSnippet,
  SUPPORT_QUICK_REPLY_KEYS,
  type SupportQuickReplyKey,
} from "../../../../helpers/supportTicketUx";
import {
  AdminSupportRecurringTheme,
  AdminSupportSummary,
  downloadAdminSupportAttachment,
  fetchAdminSupportAttachmentBlob,
  getAdminSupportAgents,
  getAdminSupportSummary,
  getAdminSupportTicket,
  getAdminSupportTickets,
  replyAdminSupportTicket,
  SupportAgentOption,
  updateAdminSupportTicket,
} from "../../../../services/superAdmin/adminSupportTicketService";
import {
  SupportTicket,
  SupportTicketAttachment,
  SupportTicketCategory,
  SupportTicketStatus,
} from "../../../../services/supportTicketService";
import { PaginationMeta } from "../../../../types/api";

const STATUSES: SupportTicketStatus[] = [
  "received",
  "triaging",
  "in_progress",
  "awaiting_tenant",
  "resolved",
  "closed",
];

const CATEGORIES: SupportTicketCategory[] = [
  "agenda",
  "orders",
  "payments",
  "account",
  "integration",
  "product_feedback",
  "other",
];

const parseCategory = (value: string | null): SupportTicketCategory | "" => {
  if (value && CATEGORIES.includes(value as SupportTicketCategory)) {
    return value as SupportTicketCategory;
  }

  return "";
};

const resolveHideSnoozedFromParams = (
  params: URLSearchParams,
  mode: "list" | "board"
): boolean => {
  const param = params.get("hide_snoozed");

  if (param === "1") {
    return true;
  }

  if (param === "0") {
    return false;
  }

  return mode === "board";
};

const OPEN_STATUSES: SupportTicketStatus[] = [
  "received",
  "triaging",
  "in_progress",
  "awaiting_tenant",
];

const BOARD_WIP_LIMIT = 8;

const isTicketSnoozed = (ticket: SupportTicket): boolean =>
  Boolean(
    ticket.snoozed_until && new Date(ticket.snoozed_until).getTime() > Date.now()
  );

const buildListSearch = (params: URLSearchParams): string => {
  const next = new URLSearchParams(params);
  const query = next.toString();

  return query ? `?${query}` : "";
};

const SuperAdminSupportTickets = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { ticketId } = useParams<{ ticketId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<SupportTicketStatus | "">("");
  const [satisfactionFilter, setSatisfactionFilter] = useState<"yes" | "no" | "">(
    searchParams.get("satisfaction") === "yes" ||
      searchParams.get("satisfaction") === "no"
      ? (searchParams.get("satisfaction") as "yes" | "no")
      : ""
  );
  const [sort, setSort] = useState<"created_at" | "sla">(
    searchParams.get("sort") === "sla" ? "sla" : "created_at"
  );
  const [order, setOrder] = useState<"asc" | "desc">(
    searchParams.get("order") === "asc" || searchParams.get("order") === "desc"
      ? (searchParams.get("order") as "asc" | "desc")
      : searchParams.get("sort") === "sla"
        ? "asc"
        : "desc"
  );
  const [noAgentReply, setNoAgentReply] = useState(
    searchParams.get("no_agent_reply") === "1"
  );
  const assigneeFromUrl = searchParams.get("assignee");
  const [assigneeFilter, setAssigneeFilter] = useState<"" | "mine" | "unassigned">(
    assigneeFromUrl === "mine" || assigneeFromUrl === "unassigned"
      ? assigneeFromUrl
      : ""
  );
  const initialViewMode =
    searchParams.get("view") === "board" ? "board" : "list";
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [searchInput, setSearchInput] = useState(searchParams.get("q") || "");
  const viewMode = searchParams.get("view") === "board" ? "board" : "list";
  const [hideSnoozed, setHideSnoozed] = useState(() =>
    resolveHideSnoozedFromParams(searchParams, initialViewMode)
  );
  const [categoryFilter, setCategoryFilter] = useState<SupportTicketCategory | "">(
    parseCategory(searchParams.get("category"))
  );
  const [hasRecurringTheme, setHasRecurringTheme] = useState(
    searchParams.get("has_recurring_theme") === "1"
  );
  const [cheatsheetOpen, setCheatsheetOpen] = useState(false);
  const [expandedBoardColumns, setExpandedBoardColumns] = useState<
    Partial<Record<SupportTicketStatus, boolean>>
  >({});
  const [agents, setAgents] = useState<SupportAgentOption[]>([]);
  const [assigningId, setAssigningId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selected, setSelected] = useState<SupportTicket | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replyBody, setReplyBody] = useState("");
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const [internalNote, setInternalNote] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [summary, setSummary] = useState<AdminSupportSummary | null>(null);
  const [currentSaId, setCurrentSaId] = useState<number | null>(null);
  const [focusIndex, setFocusIndex] = useState(0);
  const [snippetsEditing, setSnippetsEditing] = useState(false);
  const [snippetDrafts, setSnippetDrafts] = useState<
    Partial<Record<SupportQuickReplyKey, string>>
  >({});
  const [draggingTicketId, setDraggingTicketId] = useState<number | null>(null);
  const [boardSavingId, setBoardSavingId] = useState<number | null>(null);
  const [nowTick, setNowTick] = useState(0);
  const [backlogPreview, setBacklogPreview] = useState<{
    theme: AdminSupportRecurringTheme;
    markdown: string;
    githubUrl: string;
  } | null>(null);
  const selectedRef = useRef<SupportTicket | null>(null);
  const loadListRef = useRef<() => Promise<void>>(async () => {});

  selectedRef.current = selected;

  useEffect(() => {
    document.title = t("admin.supportTickets.pageTitle");
  }, [t, i18n.language]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setNowTick((current) => current + 1);
    }, 60_000);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    setCurrentSaId(resolveCurrentSuperAdminId());
  }, []);

  useEffect(() => {
    const fromUrl = searchParams.get("satisfaction");
    if (fromUrl === "yes" || fromUrl === "no") {
      setSatisfactionFilter(fromUrl);
    }

    if (searchParams.get("sort") === "sla") {
      setSort("sla");
      setOrder(searchParams.get("order") === "desc" ? "desc" : "asc");
    }

    setNoAgentReply(searchParams.get("no_agent_reply") === "1");

    const assignee = searchParams.get("assignee");
    if (assignee === "mine" || assignee === "unassigned") {
      setAssigneeFilter(assignee);
    } else {
      setAssigneeFilter("");
    }

    setSearch(searchParams.get("q") || "");
    setSearchInput(searchParams.get("q") || "");

    const mode = searchParams.get("view") === "board" ? "board" : "list";
    setHideSnoozed(resolveHideSnoozedFromParams(searchParams, mode));
    setCategoryFilter(parseCategory(searchParams.get("category")));
  }, [searchParams]);

  const syncListParams = useCallback(
    (next: {
      satisfaction?: "yes" | "no" | "";
      sort?: "created_at" | "sla";
      order?: "asc" | "desc";
      noAgentReply?: boolean;
      assignee?: "" | "mine" | "unassigned";
      searchQuery?: string;
      hideSnoozed?: boolean;
      category?: SupportTicketCategory | "";
      hasRecurringTheme?: boolean;
    }) => {
      const params = new URLSearchParams(searchParams);
      const satisfaction = next.satisfaction ?? satisfactionFilter;
      const nextSort = next.sort ?? sort;
      const nextOrder = next.order ?? order;
      const nextNoAgentReply = next.noAgentReply ?? noAgentReply;
      const nextAssignee = next.assignee ?? assigneeFilter;
      const nextSearch = next.searchQuery ?? search;
      const nextHideSnoozed = next.hideSnoozed ?? hideSnoozed;
      const nextCategory = next.category ?? categoryFilter;
      const nextHasRecurringTheme = next.hasRecurringTheme ?? hasRecurringTheme;

      if (satisfaction) {
        params.set("satisfaction", satisfaction);
      } else {
        params.delete("satisfaction");
      }

      if (nextNoAgentReply) {
        params.set("no_agent_reply", "1");
      } else {
        params.delete("no_agent_reply");
      }

      if (nextAssignee) {
        params.set("assignee", nextAssignee);
      } else {
        params.delete("assignee");
      }

      const trimmedSearch = nextSearch.trim();
      if (trimmedSearch) {
        params.set("q", trimmedSearch);
      } else {
        params.delete("q");
      }

      if (nextHideSnoozed) {
        params.set("hide_snoozed", "1");
      } else if (params.get("view") === "board" || params.get("hide_snoozed")) {
        params.set("hide_snoozed", "0");
      } else {
        params.delete("hide_snoozed");
      }

      if (nextCategory) {
        params.set("category", nextCategory);
      } else {
        params.delete("category");
      }

      if (nextHasRecurringTheme) {
        params.set("has_recurring_theme", "1");
      } else {
        params.delete("has_recurring_theme");
      }

      if (nextSort === "sla") {
        params.set("sort", "sla");
        params.set("order", nextOrder);
      } else {
        params.delete("sort");
        params.delete("order");
      }

      setSearchParams(params, { replace: true });
    },
    [
      assigneeFilter,
      categoryFilter,
      hasRecurringTheme,
      hideSnoozed,
      noAgentReply,
      order,
      satisfactionFilter,
      search,
      searchParams,
      setSearchParams,
      sort,
    ]
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (searchInput.trim() === search.trim()) {
        return;
      }

      setPage(1);
      setSearch(searchInput);
      syncListParams({ searchQuery: searchInput });
    }, 400);

    return () => window.clearTimeout(timer);
  }, [searchInput, search, syncListParams]);

  const loadList = useCallback(async () => {
    if (page > 1) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const [response, summaryResponse] = await Promise.all([
        getAdminSupportTickets({
          page,
          per_page: viewMode === "board" ? 50 : 15,
          status: statusFilter,
          category: categoryFilter || undefined,
          satisfaction: satisfactionFilter,
          no_agent_reply: noAgentReply || undefined,
          sort,
          order,
          search: search.trim() || undefined,
          unassigned: assigneeFilter === "unassigned" ? true : undefined,
          assigned_to:
            assigneeFilter === "mine" && currentSaId ? currentSaId : undefined,
          hide_snoozed: hideSnoozed ? true : undefined,
          has_recurring_theme: hasRecurringTheme ? true : undefined,
        }),
        getAdminSupportSummary().catch(() => null),
      ]);

      if (response.success) {
        setTickets((current) =>
          page > 1 ? [...current, ...(response.data || [])] : response.data || []
        );
        setMeta(response.meta || null);
      }

      if (summaryResponse?.success) {
        setSummary(summaryResponse.data);
      }
    } catch (error: unknown) {
      const apiError = error as { message?: string };
      toast.error(apiError.message || t("admin.supportTickets.toast.loadError"));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [
    assigneeFilter,
    categoryFilter,
    currentSaId,
    hasRecurringTheme,
    hideSnoozed,
    noAgentReply,
    order,
    page,
    satisfactionFilter,
    search,
    sort,
    statusFilter,
    t,
    viewMode,
  ]);

  loadListRef.current = loadList;

  useEffect(() => {
    void loadList();
  }, [loadList]);

  useEffect(() => {
    setFocusIndex((current) =>
      tickets.length === 0 ? 0 : Math.min(current, tickets.length - 1)
    );
  }, [tickets.length]);

  useEffect(() => {
    void (async () => {
      try {
        const response = await getAdminSupportAgents();

        if (response.success) {
          setAgents(response.data || []);
        }
      } catch {
        // agents list is optional for page render
      }
    })();
  }, []);

  const loadDetail = useCallback(
    async (id: number) => {
      setDetailOpen(true);
      setDetailLoading(true);
      setReplyBody("");
      setReplyFiles([]);
      setInternalNote(false);

      try {
        const response = await getAdminSupportTicket(id);

        if (response.success) {
          setSelected(response.data);
        }
      } catch (error: unknown) {
        const apiError = error as { message?: string };
        toast.error(apiError.message || t("admin.supportTickets.toast.loadError"));
        setDetailOpen(false);
        navigate(`/super-admin/support/tickets${buildListSearch(searchParams)}`);
      } finally {
        setDetailLoading(false);
      }
    },
    [navigate, searchParams, t]
  );

  useEffect(() => {
    if (ticketId) {
      void loadDetail(Number(ticketId));
    } else {
      setSelected(null);
      setDetailOpen(false);
    }
  }, [ticketId, loadDetail]);

  useEffect(() => {
    if (!detailOpen || !selected?.id) {
      return;
    }

    const ticketIdValue = selected.id;
    let cancelled = false;
    let timeoutId = 0;

    const poll = async () => {
      const current = selectedRef.current;

      if (!current || current.id !== ticketIdValue) {
        return;
      }

      try {
        const response = await getAdminSupportTicket(ticketIdValue);

        if (!response.success || !response.data || cancelled) {
          return;
        }

        const next = response.data;
        const prevMsgLen = current.messages?.length ?? 0;
        const nextMsgLen = next.messages?.length ?? 0;
        const agentAtChanged =
          (next.last_agent_message_at || "") !== (current.last_agent_message_at || "");
        const updatedChanged = next.updated_at !== current.updated_at;

        if (nextMsgLen > prevMsgLen) {
          toast.info(t("admin.supportTickets.toast.newMessage"), { autoClose: 3500 });
          playSoftNotificationBeep();
        }

        if (updatedChanged || agentAtChanged || nextMsgLen !== prevMsgLen) {
          setSelected(next);
          setTickets((list) =>
            list.map((ticket) =>
              ticket.id === ticketIdValue
                ? {
                    ...ticket,
                    status: next.status,
                    updated_at: next.updated_at,
                    last_agent_message_at: next.last_agent_message_at,
                    snoozed_until: next.snoozed_until,
                    assigned_to_super_admin_id: next.assigned_to_super_admin_id,
                  }
                : ticket
            )
          );
          void loadListRef.current();
        }
      } catch {
        // silent poll
      }
    };

    const schedule = () => {
      timeoutId = window.setTimeout(() => {
        void poll().finally(() => {
          if (!cancelled) {
            schedule();
          }
        });
      }, resolveSupportLivePollMs());
    };

    const onVisibility = () => {
      window.clearTimeout(timeoutId);

      if (document.visibilityState === "visible") {
        void poll().finally(() => {
          if (!cancelled) {
            schedule();
          }
        });
      } else {
        schedule();
      }
    };

    const onFocus = () => {
      window.clearTimeout(timeoutId);
      void poll().finally(() => {
        if (!cancelled) {
          schedule();
        }
      });
    };

    schedule();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
    };
  }, [detailOpen, selected?.id, t]);

  const openDetail = (id: number) => {
    navigate(`/super-admin/support/tickets/${id}${buildListSearch(searchParams)}`);
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setSelected(null);
    navigate(`/super-admin/support/tickets${buildListSearch(searchParams)}`);
  };

  const setListViewMode = (mode: "list" | "board") => {
    const params = new URLSearchParams(searchParams);

    if (mode === "board") {
      params.set("view", "board");
      params.set("hide_snoozed", "1");
      setHideSnoozed(true);
    } else {
      params.delete("view");
    }

    setPage(1);
    setExpandedBoardColumns({});
    setSearchParams(params, { replace: true });
  };

  const toggleAssigneeFilter = (value: "mine" | "unassigned") => {
    const next = assigneeFilter === value ? "" : value;
    setPage(1);
    setAssigneeFilter(next);
    syncListParams({ assignee: next });
  };

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;

      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") {
        return;
      }

      if (event.key === "?" && !event.shiftKey) {
        event.preventDefault();
        setCheatsheetOpen((current) => !current);
        return;
      }

      if (event.key === "Escape" && cheatsheetOpen) {
        event.preventDefault();
        setCheatsheetOpen(false);
        return;
      }

      if (detailOpen || viewMode !== "list" || tickets.length === 0) {
        return;
      }

      if (event.key === "j") {
        event.preventDefault();
        setFocusIndex((current) => Math.min(current + 1, tickets.length - 1));
      } else if (event.key === "k") {
        event.preventDefault();
        setFocusIndex((current) => Math.max(current - 1, 0));
      } else if (event.key === "Enter" && tickets[focusIndex]) {
        event.preventDefault();
        openDetail(tickets[focusIndex].id);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
    // openDetail is used only for Enter on focused row; including it rebinds listeners every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cheatsheetOpen, detailOpen, focusIndex, tickets, viewMode]);

  const openSnippetsEditor = () => {
    const custom = loadCustomSupportSnippets();
    const drafts: Partial<Record<SupportQuickReplyKey, string>> = {};

    SUPPORT_QUICK_REPLY_KEYS.forEach((key) => {
      drafts[key] =
        custom[key] ?? t(`admin.supportTickets.snippets.${key}`);
    });

    setSnippetDrafts(drafts);
    setSnippetsEditing(true);
  };

  const handleSnooze = async (hours: 1 | 4 | 24) => {
    if (!selected) {
      return;
    }

    setSaving(true);

    try {
      const response = await updateAdminSupportTicket(selected.id, {
        snooze_hours: hours,
      });

      if (response.success) {
        setSelected(response.data);
        setTickets((current) =>
          current.map((ticket) =>
            ticket.id === selected.id ? response.data : ticket
          )
        );
        toast.success(t("admin.supportTickets.snooze.set"));
        void loadList();
      }
    } catch (error: unknown) {
      const apiError = error as { message?: string };
      toast.error(apiError.message || t("admin.supportTickets.toast.updateError"));
    } finally {
      setSaving(false);
    }
  };

  const handleClearSnooze = async () => {
    if (!selected) {
      return;
    }

    setSaving(true);

    try {
      const response = await updateAdminSupportTicket(selected.id, {
        snoozed_until: null,
      });

      if (response.success) {
        setSelected(response.data);
        setTickets((current) =>
          current.map((ticket) =>
            ticket.id === selected.id ? response.data : ticket
          )
        );
        void loadList();
      }
    } catch (error: unknown) {
      const apiError = error as { message?: string };
      toast.error(apiError.message || t("admin.supportTickets.toast.updateError"));
    } finally {
      setSaving(false);
    }
  };

  const handleBoardDrop = async (
    status: SupportTicketStatus,
    ticketId: number
  ) => {
    setBoardSavingId(ticketId);

    try {
      const response = await updateAdminSupportTicket(ticketId, { status });

      if (response.success) {
        toast.success(t("admin.supportTickets.toast.updated"));
        void loadList();
      }
    } catch (error: unknown) {
      const apiError = error as { message?: string };
      toast.error(apiError.message || t("admin.supportTickets.toast.updateError"));
    } finally {
      setBoardSavingId(null);
      setDraggingTicketId(null);
    }
  };

  const toggleSlaSort = () => {
    setPage(1);

    if (sort !== "sla") {
      setSort("sla");
      setOrder("asc");
      syncListParams({ sort: "sla", order: "asc" });
      return;
    }

    const nextOrder = order === "asc" ? "desc" : "asc";
    setOrder(nextOrder);
    syncListParams({ sort: "sla", order: nextOrder });
  };

  const handleAssigneeChange = async (
    ticketId: number,
    value: string,
    updateSelected = false
  ) => {
    setAssigningId(ticketId);

    try {
      const response = await updateAdminSupportTicket(ticketId, {
        assigned_to_super_admin_id: value === "" ? null : Number(value),
      });

      if (response.success) {
        if (updateSelected && selected?.id === ticketId) {
          setSelected(response.data);
        }

        setTickets((current) =>
          current.map((ticket) =>
            ticket.id === ticketId ? response.data : ticket
          )
        );
        toast.success(t("admin.supportTickets.toast.updated"));
      }
    } catch (error: unknown) {
      const apiError = error as { message?: string };
      toast.error(apiError.message || t("admin.supportTickets.toast.updateError"));
    } finally {
      setAssigningId(null);
    }
  };

  const renderAssignToMeButton = (
    ticket: SupportTicket,
    updateSelected = false
  ) => {
    if (!currentSaId || ticket.assigned_to_super_admin_id === currentSaId) {
      return null;
    }

    return (
      <Button
        color="soft-primary"
        size="sm"
        type="button"
        disabled={assigningId === ticket.id}
        onClick={(event) => {
          event.stopPropagation();
          void handleAssigneeChange(
            ticket.id,
            String(currentSaId),
            updateSelected
          );
        }}
      >
        {t("admin.supportTickets.actions.assignToMe")}
      </Button>
    );
  };

  const handleCopyLink = async () => {
    if (!selected) {
      return;
    }

    const copied = await copySupportTicketLink("admin", selected.id);

    if (copied) {
      toast.success(t("support.toast.linkCopied"));
    } else {
      toast.error(t("admin.supportTickets.toast.updateError"));
    }
  };

  const handleStatusChange = async (status: SupportTicketStatus) => {
    if (!selected) {
      return;
    }

    setSaving(true);

    try {
      const response = await updateAdminSupportTicket(selected.id, { status });

      if (response.success) {
        setSelected(response.data);
        toast.success(t("admin.supportTickets.toast.updated"));
        void loadList();
      }
    } catch (error: unknown) {
      const apiError = error as { message?: string };
      toast.error(apiError.message || t("admin.supportTickets.toast.updateError"));
    } finally {
      setSaving(false);
    }
  };

  const handleReply = async () => {
    if (!selected || !replyBody.trim()) {
      return;
    }

    setSaving(true);

    try {
      await replyAdminSupportTicket(selected.id, {
        body: replyBody.trim(),
        is_internal_note: internalNote,
        attachments: replyFiles,
      });
      toast.success(t("admin.supportTickets.toast.replied"));
      setReplyBody("");
      setReplyFiles([]);
      await loadDetail(selected.id);
      void loadList();
    } catch (error: unknown) {
      const apiError = error as { message?: string };
      toast.error(apiError.message || t("admin.supportTickets.toast.replyError"));
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async (attachment: SupportTicketAttachment) => {
    if (!selected) {
      return;
    }

    setDownloadingId(attachment.id);

    try {
      await downloadAdminSupportAttachment(
        selected.id,
        attachment.id,
        attachment.original_filename
      );
    } catch {
      toast.error(t("admin.supportTickets.toast.downloadError"));
    } finally {
      setDownloadingId(null);
    }
  };

  const fetchBlob = useCallback(
    (ticketIdValue: number, attachmentId: number) =>
      fetchAdminSupportAttachmentBlob(ticketIdValue, attachmentId),
    []
  );

  const renderAttachments = (attachments?: SupportTicketAttachment[]) => {
    if (!selected) {
      return null;
    }

    return (
      <SupportAttachmentList
        ticketId={selected.id}
        attachments={attachments}
        fetchBlob={fetchBlob}
        onDownload={handleDownload}
        downloadingId={downloadingId}
      />
    );
  };

  const formatSnoozeLabel = (snoozedUntil: string): string => {
    void nowTick;
    const relative = formatRelativeUntil(snoozedUntil, i18n.language);

    if (relative) {
      return t("admin.supportTickets.snooze.relative", { relative });
    }

    return t("admin.supportTickets.snooze.until", {
      time: new Date(snoozedUntil).toLocaleString(),
    });
  };

  const renderSnoozeBadge = (ticket: SupportTicket) => {
    if (!isTicketSnoozed(ticket) || !ticket.snoozed_until) {
      return null;
    }

    return (
      <Badge
        color="info"
        pill
        title={new Date(ticket.snoozed_until).toLocaleString()}
      >
        <i className="ri-time-line me-1" />
        {formatSnoozeLabel(ticket.snoozed_until)}
      </Badge>
    );
  };

  const renderListSkeleton = () => (
    <div className="table-responsive">
      <table className="table align-middle mb-0">
        <thead>
          <tr>
            {[1, 2, 3, 4, 5, 6, 7, 8].map((col) => (
              <th key={col}>
                <Placeholder animation="glow">
                  <Placeholder xs={8} />
                </Placeholder>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[1, 2, 3, 4, 5].map((row) => (
            <tr key={row}>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((col) => (
                <td key={col}>
                  <Placeholder animation="glow">
                    <Placeholder xs={col % 3 === 0 ? 4 : 8} />
                  </Placeholder>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderBoardSkeleton = () => (
    <div
      className="d-flex gap-3 overflow-auto pb-2"
      style={{ minHeight: "420px" }}
    >
      {STATUSES.map((status) => (
        <div key={status} className="flex-shrink-0" style={{ width: "280px" }}>
          <Placeholder animation="glow" className="mb-2">
            <Placeholder xs={6} />
          </Placeholder>
          {[1, 2, 3].map((card) => (
            <Card key={card} className="mb-2">
              <CardBody className="p-2">
                <Placeholder animation="glow">
                  <Placeholder xs={4} className="mb-2" />
                  <Placeholder xs={8} className="mb-2" />
                  <Placeholder xs={6} />
                </Placeholder>
              </CardBody>
            </Card>
          ))}
        </div>
      ))}
    </div>
  );

  const renderSlaCell = (ticket: SupportTicket) => {
    const sla = resolveAgentSla(ticket.last_agent_message_at);

    if (!ticket.last_agent_message_at) {
      return (
        <Badge color="danger" pill>
          {t("admin.supportTickets.sla.never")}
        </Badge>
      );
    }

    if (!sla) {
      return "—";
    }

    const color =
      sla.tone === "critical"
        ? "danger"
        : sla.tone === "warn"
          ? "warning"
          : "success";

    return (
      <Badge color={color} pill title={new Date(ticket.last_agent_message_at).toLocaleString()}>
        {formatSlaLabel(sla.hours)}
      </Badge>
    );
  };

  const handleRecurringThemeClick = (theme: AdminSupportRecurringTheme) => {
    const nextCategory = parseCategory(theme.category);
    setPage(1);
    setSearchInput(theme.subject);
    setSearch(theme.subject);
    if (nextCategory) {
      setCategoryFilter(nextCategory);
      syncListParams({ searchQuery: theme.subject, category: nextCategory });
    } else {
      syncListParams({ searchQuery: theme.subject });
    }
  };

  const handleCopyRecurringThemes = async () => {
    if (!summary?.recurring_themes?.length) {
      return;
    }

    const markdown = summary.recurring_themes
      .map(
        (theme) =>
          `- ${theme.subject} (${theme.count}×, ${t(`support.categories.${theme.category}`)})`
      )
      .join("\n");
    const copied = await copyTextToClipboard(markdown);

    if (copied) {
      toast.success(t("admin.supportTickets.summary.themesCopied"));
    }
  };

  const openRegisterThemePreview = (theme: AdminSupportRecurringTheme) => {
    const payload = {
      subject: theme.subject,
      category: theme.category,
      categoryLabel: t(`support.categories.${theme.category}`),
      count: theme.count,
      suggestedId: summary?.suggested_next_adj_id ?? null,
    };

    setBacklogPreview({
      theme,
      markdown: buildRecurringThemeBacklogMarkdown(payload),
      githubUrl: buildRecurringThemeGithubIssueUrl(payload),
    });
  };

  const handleCopyBacklogPreview = async () => {
    if (!backlogPreview) {
      return;
    }

    const copied = await copyTextToClipboard(backlogPreview.markdown);

    if (copied) {
      toast.success(t("admin.supportTickets.summary.themeRegisteredCopied"));
      setBacklogPreview(null);
    } else {
      toast.error(t("admin.supportTickets.summary.themeRegisterCopyError"));
    }
  };

  return (
    <div className="page-content">
      <ToastContainer />
      <Container fluid>
        <BreadCrumb
          title={t("admin.supportTickets.breadcrumb.title")}
          pageTitle={t("admin.supportTickets.breadcrumb.pageTitle")}
        />

        {summary ? (
          <>
            <Row className="mb-3">
              <Col md={3}>
                <Card className="card-animate mb-0">
                  <CardBody className="d-flex justify-content-between align-items-center">
                    <div>
                      <p className="text-muted mb-1">
                        {t("admin.supportTickets.summary.awaiting")}
                      </p>
                      <h4 className="mb-0">{summary.awaiting_tenant}</h4>
                    </div>
                    <Button
                      color="soft-warning"
                      size="sm"
                      onClick={() => {
                        setPage(1);
                        setStatusFilter("awaiting_tenant");
                      }}
                    >
                      {t("admin.supportTickets.summary.filter")}
                    </Button>
                  </CardBody>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="card-animate mb-0">
                  <CardBody className="d-flex justify-content-between align-items-center">
                    <div>
                      <p className="text-muted mb-1">
                        {t("admin.supportTickets.summary.csatNo")}
                      </p>
                      <h4 className="mb-0">{summary.satisfaction_no_open}</h4>
                    </div>
                    <Button
                      color="soft-danger"
                      size="sm"
                      onClick={() => {
                        setPage(1);
                        setStatusFilter("");
                        setSatisfactionFilter("no");
                        syncListParams({ satisfaction: "no" });
                      }}
                    >
                      {t("admin.supportTickets.summary.filter")}
                    </Button>
                  </CardBody>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="card-animate mb-0">
                  <CardBody className="d-flex justify-content-between align-items-center">
                    <div>
                      <p className="text-muted mb-1">
                        {t("admin.supportTickets.summary.myOpen")}
                      </p>
                      <h4 className="mb-0">{summary.my_open}</h4>
                    </div>
                    <Button
                      color="soft-primary"
                      size="sm"
                      onClick={() => {
                        setPage(1);
                        setAssigneeFilter("mine");
                        syncListParams({ assignee: "mine" });
                      }}
                    >
                      {t("admin.supportTickets.summary.filter")}
                    </Button>
                  </CardBody>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="card-animate mb-0">
                  <CardBody className="d-flex justify-content-between align-items-center">
                    <div>
                      <p className="text-muted mb-1">
                        {t("admin.supportTickets.summary.unassignedOpen")}
                      </p>
                      <h4 className="mb-0">{summary.unassigned_open}</h4>
                      {summary.avg_first_response_hours != null ? (
                        <small className="text-muted">
                          {t("admin.supportTickets.summary.avgFirstResponse", {
                            hours: summary.avg_first_response_hours.toFixed(1),
                          })}
                        </small>
                      ) : null}
                    </div>
                    <Button
                      color="soft-secondary"
                      size="sm"
                      onClick={() => {
                        setPage(1);
                        setAssigneeFilter("unassigned");
                        syncListParams({ assignee: "unassigned" });
                      }}
                    >
                      {t("admin.supportTickets.summary.filter")}
                    </Button>
                  </CardBody>
                </Card>
              </Col>
            </Row>
            {viewMode === "board" && summary.by_status ? (
              <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
                {summary.avg_first_response_hours != null ? (
                  <small className="text-muted">
                    {t("admin.supportTickets.summary.avgFirstResponse", {
                      hours: summary.avg_first_response_hours.toFixed(1),
                    })}
                  </small>
                ) : null}
                {OPEN_STATUSES.map((status) => (
                  <Badge key={status} color="light" className="text-dark">
                    {t(`support.statuses.${status}`)}:{" "}
                    {summary.by_status[status] ?? 0}
                  </Badge>
                ))}
              </div>
            ) : null}
            {summary.by_category &&
            Object.values(summary.by_category).some((count) => (count ?? 0) > 0) ? (
              <div className="mb-3">
                <small className="text-muted d-block mb-2">
                  {t("admin.supportTickets.summary.byCategory")}
                </small>
                <div className="d-flex flex-wrap gap-2 align-items-center">
                  {CATEGORIES.filter(
                    (category) => (summary.by_category?.[category] ?? 0) > 0
                  ).map((category) => (
                    <Button
                      key={category}
                      color={categoryFilter === category ? "primary" : "soft-primary"}
                      size="sm"
                      type="button"
                      onClick={() => {
                        const next = categoryFilter === category ? "" : category;
                        setPage(1);
                        setCategoryFilter(next);
                        syncListParams({ category: next });
                      }}
                    >
                      {t(`support.categories.${category}`)} (
                      {summary.by_category?.[category] ?? 0})
                    </Button>
                  ))}
                  {categoryFilter ? (
                    <Button
                      color="light"
                      size="sm"
                      type="button"
                      onClick={() => {
                        setPage(1);
                        setCategoryFilter("");
                        syncListParams({ category: "" });
                      }}
                    >
                      {t("admin.supportTickets.filters.allCategories")}
                    </Button>
                  ) : null}
                </div>
              </div>
            ) : null}
            {summary.recurring_themes?.length ? (
              <div className="mb-3">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                  <small className="text-muted mb-0">
                    {t("admin.supportTickets.summary.recurringThemes")}
                  </small>
                  <div className="d-flex flex-wrap gap-2">
                    <Button
                      color={hasRecurringTheme ? "info" : "soft-info"}
                      size="sm"
                      type="button"
                      onClick={() => {
                        const next = !hasRecurringTheme;
                        setPage(1);
                        setHasRecurringTheme(next);
                        syncListParams({ hasRecurringTheme: next });
                      }}
                    >
                      {t("admin.supportTickets.filters.hasRecurringTheme")}
                    </Button>
                    <Button
                      color="soft-secondary"
                      size="sm"
                      type="button"
                      onClick={() => void handleCopyRecurringThemes()}
                    >
                      {t("admin.supportTickets.summary.copyThemes")}
                    </Button>
                  </div>
                </div>
                <div className="d-flex flex-column gap-2 mb-2">
                  {summary.recurring_themes.map((theme, index) => (
                    <div
                      key={`${theme.subject}-${index}`}
                      className="d-flex flex-wrap align-items-center gap-2"
                    >
                      <Button
                        color="soft-info"
                        size="sm"
                        type="button"
                        title={theme.subject}
                        onClick={() => handleRecurringThemeClick(theme)}
                      >
                        <span
                          className="text-truncate d-inline-block"
                          style={{ maxWidth: "200px" }}
                        >
                          {theme.subject}
                        </span>
                        <Badge color="secondary" pill className="ms-1">
                          {theme.count}
                        </Badge>
                      </Button>
                      <Button
                        color="soft-primary"
                        size="sm"
                        type="button"
                        title={t("admin.supportTickets.summary.registerInDocsHint")}
                        onClick={() => openRegisterThemePreview(theme)}
                      >
                        <i className="ri-file-list-3-line me-1" />
                        {t("admin.supportTickets.summary.registerInDocs")}
                      </Button>
                    </div>
                  ))}
                </div>
                <small className="text-muted">
                  {t("admin.supportTickets.summary.recurringThemesHint")}
                </small>
              </div>
            ) : null}
          </>
        ) : null}

        <Card>
          <CardHeader className="d-flex flex-wrap gap-2 align-items-end justify-content-between">
            <div className="d-flex flex-wrap align-items-center gap-2">
              <h5 className="card-title mb-0">{t("admin.supportTickets.cardTitle")}</h5>
              <div className="btn-group btn-group-sm" role="group">
                <Button
                  color={viewMode === "list" ? "primary" : "light"}
                  size="sm"
                  type="button"
                  onClick={() => setListViewMode("list")}
                >
                  {t("admin.supportTickets.board.list")}
                </Button>
                <Button
                  color={viewMode === "board" ? "primary" : "light"}
                  size="sm"
                  type="button"
                  onClick={() => setListViewMode("board")}
                >
                  {t("admin.supportTickets.board.board")}
                </Button>
              </div>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <FormGroup className="mb-0">
                <Label for="admin-support-search" className="mb-1">
                  {t("admin.supportTickets.columns.subject")}
                </Label>
                <Input
                  id="admin-support-search"
                  type="search"
                  value={searchInput}
                  placeholder={t("admin.supportTickets.columns.subject")}
                  onChange={(event) => handleSearchChange(event.target.value)}
                />
              </FormGroup>
              <FormGroup className="mb-0">
                <Label for="admin-support-status" className="mb-1">
                  {t("admin.supportTickets.filters.status")}
                </Label>
                <Input
                  id="admin-support-status"
                  type="select"
                  value={statusFilter}
                  onChange={(event) => {
                    setPage(1);
                    setStatusFilter(event.target.value as SupportTicketStatus | "");
                  }}
                >
                  <option value="">{t("admin.supportTickets.filters.all")}</option>
                  {STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {t(`support.statuses.${status}`)}
                    </option>
                  ))}
                </Input>
              </FormGroup>
              <FormGroup className="mb-0">
                <Label for="admin-support-satisfaction" className="mb-1">
                  {t("admin.supportTickets.filters.satisfaction")}
                </Label>
                <Input
                  id="admin-support-satisfaction"
                  type="select"
                  value={satisfactionFilter}
                  onChange={(event) => {
                    const value = event.target.value as "yes" | "no" | "";
                    setPage(1);
                    setSatisfactionFilter(value);
                    syncListParams({ satisfaction: value });
                  }}
                >
                  <option value="">{t("admin.supportTickets.filters.all")}</option>
                  <option value="yes">{t("admin.supportTickets.satisfaction.yes")}</option>
                  <option value="no">{t("admin.supportTickets.satisfaction.no")}</option>
                </Input>
              </FormGroup>
              <div className="d-flex align-items-end gap-1 mb-0 flex-wrap">
                {categoryFilter ? (
                  <>
                    <Badge color="primary" className="mb-1">
                      {t("admin.supportTickets.filters.activeCategory", {
                        name: t(`support.categories.${categoryFilter}`),
                      })}
                    </Badge>
                    <Button
                      color="light"
                      size="sm"
                      className="mb-1"
                      type="button"
                      aria-label={t("admin.supportTickets.filters.allCategories")}
                      onClick={() => {
                        setPage(1);
                        setCategoryFilter("");
                        syncListParams({ category: "" });
                      }}
                    >
                      <i className="ri-close-line" />
                    </Button>
                  </>
                ) : null}
                <Button
                  color={assigneeFilter === "mine" ? "primary" : "soft-primary"}
                  size="sm"
                  className="mb-1"
                  onClick={() => toggleAssigneeFilter("mine")}
                >
                  {t("admin.supportTickets.filters.mine")}
                </Button>
                <Button
                  color={
                    assigneeFilter === "unassigned" ? "primary" : "soft-secondary"
                  }
                  size="sm"
                  className="mb-1"
                  onClick={() => toggleAssigneeFilter("unassigned")}
                >
                  {t("admin.supportTickets.filters.unassigned")}
                </Button>
                <FormGroup check className="mb-1 ms-1">
                  <Input
                    id="admin-support-hide-snoozed"
                    type="checkbox"
                    checked={hideSnoozed}
                    onChange={(event) => {
                      setPage(1);
                      setHideSnoozed(event.target.checked);
                      syncListParams({ hideSnoozed: event.target.checked });
                    }}
                  />
                  <Label check for="admin-support-hide-snoozed" className="small">
                    {t("admin.supportTickets.filters.hideSnoozed")}
                  </Label>
                </FormGroup>
                <Button
                  color={hasRecurringTheme ? "info" : "soft-info"}
                  size="sm"
                  className="mb-1"
                  type="button"
                  onClick={() => {
                    const next = !hasRecurringTheme;
                    setPage(1);
                    setHasRecurringTheme(next);
                    syncListParams({ hasRecurringTheme: next });
                  }}
                >
                  {t("admin.supportTickets.filters.hasRecurringTheme")}
                </Button>
                {noAgentReply ? (
                  <>
                    <Badge color="warning" className="mb-1">
                      {t("admin.supportTickets.filters.noAgentReply")}
                    </Badge>
                    <Button
                      color="light"
                      size="sm"
                      className="mb-1"
                      onClick={() => {
                        setPage(1);
                        setNoAgentReply(false);
                        syncListParams({ noAgentReply: false });
                      }}
                    >
                      <i className="ri-close-line" />
                    </Button>
                  </>
                ) : (
                  <Button
                    color="soft-warning"
                    size="sm"
                    className="mb-1"
                    onClick={() => {
                      setPage(1);
                      setNoAgentReply(true);
                      setSort("sla");
                      setOrder("asc");
                      syncListParams({
                        noAgentReply: true,
                        sort: "sla",
                        order: "asc",
                      });
                    }}
                  >
                    {t("admin.supportTickets.filters.noAgentReply")}
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardBody>
            {viewMode === "board" ? (
              <p className="text-muted small mb-3">
                {t("admin.supportTickets.board.liteHint")}
              </p>
            ) : null}
            {loading && tickets.length === 0 ? (
              viewMode === "board" ? renderBoardSkeleton() : renderListSkeleton()
            ) : tickets.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-muted mb-2">
                  {hasRecurringTheme
                    ? t("admin.supportTickets.emptyRecurringTheme")
                    : t("admin.supportTickets.empty")}
                </p>
                {hasRecurringTheme ? (
                  <Button
                    color="soft-secondary"
                    size="sm"
                    type="button"
                    onClick={() => {
                      setPage(1);
                      setHasRecurringTheme(false);
                      syncListParams({ hasRecurringTheme: false });
                    }}
                  >
                    {t("admin.supportTickets.filters.clearRecurringTheme")}
                  </Button>
                ) : null}
              </div>
            ) : (
              <div
                className={
                  loading && tickets.length > 0
                    ? "opacity-75 position-relative"
                    : undefined
                }
              >
                {loading && tickets.length > 0 ? (
                  <div className="position-absolute top-0 end-0 p-1">
                    <Spinner size="sm" />
                  </div>
                ) : null}
                {viewMode === "board" ? (
              <div
                className="d-flex gap-3 overflow-auto pb-2"
                style={{ minHeight: "420px" }}
              >
                {STATUSES.map((status) => {
                  const columnTickets = tickets.filter(
                    (ticket) => ticket.status === status
                  );
                  const columnExpanded = expandedBoardColumns[status];
                  const visibleTickets = columnExpanded
                    ? columnTickets
                    : columnTickets.slice(0, BOARD_WIP_LIMIT);
                  const hiddenCount = columnTickets.length - visibleTickets.length;
                  const statusTotal = summary?.by_status?.[status] ?? columnTickets.length;
                  const wipOver = statusTotal > BOARD_WIP_LIMIT;

                  return (
                    <div
                      key={status}
                      className="flex-shrink-0"
                      style={{ width: "280px" }}
                      onDragOver={(event) => {
                        event.preventDefault();
                        event.dataTransfer.dropEffect = "move";
                      }}
                      onDrop={(event) => {
                        event.preventDefault();
                        const rawId = event.dataTransfer.getData("text/plain");
                        const ticketId = Number(rawId);

                        if (!Number.isFinite(ticketId)) {
                          return;
                        }

                        const ticket = tickets.find((entry) => entry.id === ticketId);

                        if (!ticket || ticket.status === status) {
                          return;
                        }

                        void handleBoardDrop(status, ticketId);
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">{t(`support.statuses.${status}`)}</h6>
                        <div className="d-flex align-items-center gap-1">
                          {wipOver ? (
                            <Badge color="warning" pill title={t("admin.supportTickets.board.wipOver", { limit: BOARD_WIP_LIMIT })}>
                              {t("admin.supportTickets.board.wipOver", {
                                limit: BOARD_WIP_LIMIT,
                              })}
                            </Badge>
                          ) : null}
                          <Badge color="secondary" pill>
                            {columnTickets.length}
                          </Badge>
                        </div>
                      </div>
                      <div className="d-flex flex-column gap-2">
                        {visibleTickets.map((ticket) => {
                          const snoozed = isTicketSnoozed(ticket);

                          return (
                          <Card
                            key={ticket.id}
                            className={`mb-0 shadow-sm ${
                              draggingTicketId === ticket.id ? "opacity-50" : ""
                            } ${boardSavingId === ticket.id ? "border-primary" : ""} ${
                              snoozed
                                ? "border border-info border-opacity-50 bg-soft-info"
                                : ""
                            }`}
                            style={
                              snoozed && draggingTicketId !== ticket.id
                                ? { opacity: 0.72 }
                                : undefined
                            }
                            draggable
                            role="button"
                            onDragStart={(event) => {
                              setDraggingTicketId(ticket.id);
                              event.dataTransfer.setData(
                                "text/plain",
                                String(ticket.id)
                              );
                              event.dataTransfer.effectAllowed = "move";
                            }}
                            onDragEnd={() => setDraggingTicketId(null)}
                            onClick={() => openDetail(ticket.id)}
                          >
                            <CardBody className="p-2">
                              <div className="d-flex justify-content-between align-items-start gap-1">
                                <div className="fw-medium small">
                                  #{ticket.public_number}
                                </div>
                                {snoozed ? (
                                  <i
                                    className="ri-moon-clear-line text-info"
                                    title={t("admin.supportTickets.snooze.label")}
                                    aria-hidden
                                  />
                                ) : null}
                              </div>
                              <div className="small text-truncate" title={ticket.subject}>
                                {ticket.subject}
                              </div>
                              <div className="text-muted small text-truncate">
                                {ticket.tenant?.name || "—"}
                              </div>
                              <div className="d-flex align-items-center gap-1 mt-2 flex-wrap">
                                {renderSlaCell(ticket)}
                                {renderSnoozeBadge(ticket)}
                                {(ticket.attachments_count ?? 0) > 0 ? (
                                  <Badge color="light" className="text-muted">
                                    <i className="ri-attachment-2 me-1" />
                                    {ticket.attachments_count}
                                  </Badge>
                                ) : null}
                              </div>
                            </CardBody>
                          </Card>
                          );
                        })}
                        {hiddenCount > 0 ? (
                          <Button
                            color="light"
                            size="sm"
                            type="button"
                            onClick={() =>
                              setExpandedBoardColumns((current) => ({
                                ...current,
                                [status]: true,
                              }))
                            }
                          >
                            {t("admin.supportTickets.board.showMore", {
                              count: hiddenCount,
                            })}
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>{t("admin.supportTickets.columns.tenant")}</th>
                      <th>{t("admin.supportTickets.columns.subject")}</th>
                      <th>{t("admin.supportTickets.columns.category")}</th>
                      <th>{t("admin.supportTickets.columns.status")}</th>
                      <th>
                        <button
                          type="button"
                          className="btn btn-link p-0 text-decoration-none fw-semibold"
                          onClick={toggleSlaSort}
                          title={t("admin.supportTickets.sla.sortHint")}
                        >
                          {t("admin.supportTickets.columns.sla")}
                          {sort === "sla" ? (
                            <i
                              className={`ri-arrow-${order === "asc" ? "up" : "down"}-s-line ms-1`}
                            />
                          ) : (
                            <i className="ri-arrow-up-down-line ms-1 text-muted" />
                          )}
                        </button>
                      </th>
                      <th>{t("admin.supportTickets.columns.attachments")}</th>
                      <th>{t("admin.supportTickets.columns.assignee")}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {tickets.map((ticket, index) => (
                      <tr
                        key={ticket.id}
                        className={
                          !detailOpen && focusIndex === index ? "table-active" : undefined
                        }
                      >
                        <td>{ticket.public_number}</td>
                        <td>{ticket.tenant?.name || "—"}</td>
                        <td>{ticket.subject}</td>
                        <td>{t(`support.categories.${ticket.category}`)}</td>
                        <td>
                          <Badge color="secondary">
                            {t(`support.statuses.${ticket.status}`)}
                          </Badge>
                        </td>
                        <td>{renderSlaCell(ticket)}</td>
                        <td>
                          <div className="d-flex flex-wrap gap-1 align-items-center">
                            {(ticket.attachments_count ?? 0) > 0 ? (
                              <Badge color="soft-secondary" pill>
                                {ticket.attachments_count}
                              </Badge>
                            ) : (
                              "—"
                            )}
                            {renderSnoozeBadge(ticket)}
                          </div>
                        </td>
                        <td onClick={(event) => event.stopPropagation()}>
                          <div className="d-flex align-items-center gap-1 flex-wrap">
                            <Input
                              type="select"
                              bsSize="sm"
                              value={ticket.assigned_to_super_admin_id ?? ""}
                              disabled={assigningId === ticket.id}
                              onClick={(event) => event.stopPropagation()}
                              onChange={(event) => {
                                event.stopPropagation();
                                void handleAssigneeChange(ticket.id, event.target.value);
                              }}
                            >
                              <option value="">
                                {t("admin.supportTickets.assignee.unassigned")}
                              </option>
                              {agents.map((agent) => (
                                <option key={agent.id} value={agent.id}>
                                  {agent.name}
                                </option>
                              ))}
                            </Input>
                            {renderAssignToMeButton(ticket)}
                            {assigningId === ticket.id ? <Spinner size="sm" /> : null}
                          </div>
                        </td>
                        <td className="text-end">
                          <Button
                            color="soft-primary"
                            size="sm"
                            onClick={() => openDetail(ticket.id)}
                          >
                            {t("admin.supportTickets.actions.open")}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
                )}
              </div>
            )}

            {meta && meta.current_page < meta.last_page ? (
              <div className="text-center mt-3">
                <Button
                  color="soft-primary"
                  size="sm"
                  disabled={loadingMore}
                  onClick={() => setPage((current) => current + 1)}
                >
                  {loadingMore ? (
                    <Spinner size="sm" />
                  ) : (
                    t("admin.supportTickets.board.loadMore")
                  )}
                </Button>
              </div>
            ) : null}
          </CardBody>
        </Card>
      </Container>

      <Modal isOpen={detailOpen} toggle={closeDetail} size="lg" centered>
        <ModalHeader toggle={closeDetail}>
          <div className="d-flex align-items-center justify-content-between w-100 me-3 gap-2">
            <span>
              {selected
                ? `#${selected.public_number} — ${selected.subject}`
                : t("admin.supportTickets.detailTitle")}
            </span>
            {selected ? (
              <Button
                color="soft-secondary"
                size="sm"
                type="button"
                onClick={() => void handleCopyLink()}
              >
                {t("admin.supportTickets.actions.copyLink")}
              </Button>
            ) : null}
          </div>
        </ModalHeader>
        <ModalBody>
          {detailLoading || !selected ? (
            <div className="text-center py-4">
              <Spinner />
            </div>
          ) : (
            <>
              <Row className="mb-3 g-2">
                <Col md={6}>
                  <Label>{t("admin.supportTickets.columns.tenant")}</Label>
                  <div>{selected.tenant?.name || "—"}</div>
                </Col>
                <Col md={6}>
                  <Label for="admin-ticket-status">
                    {t("admin.supportTickets.columns.status")}
                  </Label>
                  <Input
                    id="admin-ticket-status"
                    type="select"
                    value={selected.status}
                    disabled={saving}
                    onChange={(event) =>
                      void handleStatusChange(event.target.value as SupportTicketStatus)
                    }
                  >
                    {STATUSES.map((status) => (
                      <option key={status} value={status}>
                        {t(`support.statuses.${status}`)}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md={6}>
                  <Label for="admin-ticket-assignee">
                    {t("admin.supportTickets.columns.assignee")}
                  </Label>
                  <div className="d-flex align-items-center gap-1 flex-wrap">
                    <Input
                      id="admin-ticket-assignee"
                      type="select"
                      value={selected.assigned_to_super_admin_id ?? ""}
                      disabled={saving || assigningId === selected.id}
                      onChange={(event) =>
                        void handleAssigneeChange(
                          selected.id,
                          event.target.value,
                          true
                        )
                      }
                    >
                      <option value="">
                        {t("admin.supportTickets.assignee.unassigned")}
                      </option>
                      {agents.map((agent) => (
                        <option key={agent.id} value={agent.id}>
                          {agent.name}
                        </option>
                      ))}
                    </Input>
                    {renderAssignToMeButton(selected, true)}
                    {assigningId === selected.id ? <Spinner size="sm" /> : null}
                  </div>
                </Col>
              </Row>

              <div className="border rounded p-2 mb-3 bg-light">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                  <Label className="mb-0">{t("admin.supportTickets.snooze.label")}</Label>
                  {selected.snoozed_until &&
                  new Date(selected.snoozed_until).getTime() > Date.now() ? (
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <small
                        className="text-muted"
                        title={new Date(selected.snoozed_until).toLocaleString()}
                      >
                        {formatSnoozeLabel(selected.snoozed_until)}
                      </small>
                      <Button
                        color="link"
                        size="sm"
                        className="p-0"
                        type="button"
                        disabled={saving}
                        onClick={() => void handleClearSnooze()}
                      >
                        {t("admin.supportTickets.snooze.clear")}
                      </Button>
                    </div>
                  ) : null}
                </div>
                <div className="d-flex flex-wrap gap-1">
                  <Button
                    color="soft-secondary"
                    size="sm"
                    type="button"
                    disabled={saving}
                    onClick={() => void handleSnooze(1)}
                  >
                    {t("admin.supportTickets.snooze.in1h")}
                  </Button>
                  <Button
                    color="soft-secondary"
                    size="sm"
                    type="button"
                    disabled={saving}
                    onClick={() => void handleSnooze(4)}
                  >
                    {t("admin.supportTickets.snooze.in4h")}
                  </Button>
                  <Button
                    color="soft-secondary"
                    size="sm"
                    type="button"
                    disabled={saving}
                    onClick={() => void handleSnooze(24)}
                  >
                    {t("admin.supportTickets.snooze.tomorrow")}
                  </Button>
                </div>
              </div>

              <p className="text-muted">{selected.description}</p>
              {(() => {
                const sla = resolveAgentSla(selected.last_agent_message_at);
                if (!sla || !selected.last_agent_message_at) {
                  return null;
                }

                const color =
                  sla.tone === "critical"
                    ? "danger"
                    : sla.tone === "warn"
                      ? "warning"
                      : "success";

                return (
                  <p className={`small text-${color}`}>
                    <i className="ri-time-line me-1" />
                    {t("support.sla.sinceAgent", {
                      time: new Date(selected.last_agent_message_at).toLocaleString(),
                    })}
                    {sla.tone === "critical"
                      ? ` — ${t("support.sla.critical")}`
                      : sla.tone === "warn"
                        ? ` — ${t("support.sla.warn")}`
                        : ""}
                  </p>
                );
              })()}
              {selected.satisfaction ? (
                <p className="small mb-2">
                  <Badge color={selected.satisfaction === "yes" ? "success" : "warning"}>
                    {selected.satisfaction === "yes"
                      ? t("admin.supportTickets.satisfaction.yes")
                      : t("admin.supportTickets.satisfaction.no")}
                  </Badge>
                  {selected.satisfaction_comment
                    ? ` — ${selected.satisfaction_comment}`
                    : null}
                </p>
              ) : null}
              {renderAttachments(selected.attachments)}

              <h6 className="mt-3">{t("support.threadTitle")}</h6>
              <div className="d-flex flex-column gap-2 mb-3">
                {(selected.messages || []).map((message) => (
                  <div
                    key={message.id}
                    className={`border rounded p-2 ${
                      message.is_internal_note ? "border-warning" : ""
                    }`}
                  >
                    <div className="d-flex justify-content-between">
                      <strong>
                        {message.is_internal_note
                          ? t("admin.supportTickets.internalNote")
                          : message.author_super_admin?.name ||
                            message.author_user?.name ||
                            message.author_type}
                      </strong>
                      <small className="text-muted">
                        {message.created_at
                          ? new Date(message.created_at).toLocaleString()
                          : ""}
                      </small>
                    </div>
                    <div style={{ whiteSpace: "pre-wrap" }}>{message.body}</div>
                    {renderAttachments(message.attachments)}
                  </div>
                ))}
              </div>

              <FormGroup>
                <Label for="admin-reply">{t("admin.supportTickets.replyLabel")}</Label>
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                  <div className="d-flex flex-wrap gap-1">
                    {SUPPORT_QUICK_REPLY_KEYS.map((key: SupportQuickReplyKey) => (
                      <Button
                        key={key}
                        color="soft-secondary"
                        size="sm"
                        type="button"
                        disabled={snippetsEditing}
                        onClick={() =>
                          setReplyBody(
                            resolveSupportSnippetBody(
                              key,
                              t(`admin.supportTickets.snippets.${key}`)
                            )
                          )
                        }
                      >
                        {t(`admin.supportTickets.snippetLabels.${key}`)}
                      </Button>
                    ))}
                  </div>
                  <Button
                    color="link"
                    size="sm"
                    className="p-0"
                    type="button"
                    onClick={() => {
                      if (snippetsEditing) {
                        setSnippetsEditing(false);
                        return;
                      }

                      openSnippetsEditor();
                    }}
                  >
                    {snippetsEditing
                      ? t("admin.supportTickets.actions.close")
                      : t("admin.supportTickets.snippets.edit")}
                  </Button>
                </div>
                {snippetsEditing ? (
                  <div className="border rounded p-2 mb-2 bg-light">
                    <p className="small fw-semibold mb-2">
                      {t("admin.supportTickets.snippets.editingTitle")}
                    </p>
                    {SUPPORT_QUICK_REPLY_KEYS.map((key: SupportQuickReplyKey) => (
                      <div key={key} className="mb-2">
                        <Label className="small mb-1">
                          {t(`admin.supportTickets.snippetLabels.${key}`)}
                        </Label>
                        <Input
                          type="textarea"
                          rows={2}
                          value={snippetDrafts[key] ?? ""}
                          onChange={(event) =>
                            setSnippetDrafts((current) => ({
                              ...current,
                              [key]: event.target.value,
                            }))
                          }
                        />
                        <div className="d-flex gap-1 mt-1">
                          <Button
                            color="soft-primary"
                            size="sm"
                            type="button"
                            onClick={() => {
                              saveCustomSupportSnippet(
                                key,
                                snippetDrafts[key] ?? ""
                              );
                              toast.success(t("admin.supportTickets.toast.updated"));
                            }}
                          >
                            {t("admin.supportTickets.snippets.save")}
                          </Button>
                          <Button
                            color="light"
                            size="sm"
                            type="button"
                            onClick={() => {
                              clearCustomSupportSnippet(key);
                              setSnippetDrafts((current) => ({
                                ...current,
                                [key]: t(`admin.supportTickets.snippets.${key}`),
                              }));
                            }}
                          >
                            {t("admin.supportTickets.snippets.reset")}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
                <Input
                  id="admin-reply"
                  type="textarea"
                  rows={3}
                  value={replyBody}
                  onChange={(event) => setReplyBody(event.target.value)}
                />
              </FormGroup>
              {!internalNote ? (
                <FormGroup>
                  <Label for="admin-reply-files">{t("support.fields.attachment")}</Label>
                  <Input
                    id="admin-reply-files"
                    type="file"
                    multiple
                    accept=".png,.jpg,.jpeg,.pdf"
                    onChange={(event) => {
                      const files = Array.from(event.target.files || []).slice(0, 3);
                      setReplyFiles(files);
                    }}
                  />
                  <small className="text-muted">{t("support.fields.attachmentHint")}</small>
                </FormGroup>
              ) : null}
              <FormGroup check className="mb-0">
                <Input
                  id="admin-internal-note"
                  type="checkbox"
                  checked={internalNote}
                  onChange={(event) => setInternalNote(event.target.checked)}
                />
                <Label check for="admin-internal-note">
                  {t("admin.supportTickets.internalNoteToggle")}
                </Label>
              </FormGroup>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={closeDetail}>
            {t("admin.supportTickets.actions.close")}
          </Button>
          <Button
            color="primary"
            disabled={saving || !replyBody.trim() || !selected}
            onClick={() => void handleReply()}
          >
            {saving ? <Spinner size="sm" /> : t("admin.supportTickets.sendReply")}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={cheatsheetOpen}
        toggle={() => setCheatsheetOpen(false)}
        centered
      >
        <ModalHeader toggle={() => setCheatsheetOpen(false)}>
          {t("admin.supportTickets.cheatsheet.title")}
        </ModalHeader>
        <ModalBody>
          <ul className="mb-0 ps-3">
            <li>{t("admin.supportTickets.cheatsheet.items.j")}</li>
            <li>{t("admin.supportTickets.cheatsheet.items.k")}</li>
            <li>{t("admin.supportTickets.cheatsheet.items.enter")}</li>
            <li>{t("admin.supportTickets.cheatsheet.items.question")}</li>
            <li>{t("admin.supportTickets.cheatsheet.items.board")}</li>
          </ul>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setCheatsheetOpen(false)}>
            {t("admin.supportTickets.cheatsheet.close")}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={Boolean(backlogPreview)}
        toggle={() => setBacklogPreview(null)}
        centered
        size="lg"
      >
        <ModalHeader toggle={() => setBacklogPreview(null)}>
          {t("admin.supportTickets.summary.registerPreviewTitle")}
        </ModalHeader>
        <ModalBody>
          {backlogPreview ? (
            <>
              <p className="text-muted small">
                {t("admin.supportTickets.summary.registerPreviewHint", {
                  id:
                    summary?.suggested_next_adj_id ||
                    t("admin.supportTickets.summary.registerPreviewIdUnknown"),
                })}
              </p>
              <Input
                type="textarea"
                rows={12}
                readOnly
                value={backlogPreview.markdown}
                className="font-monospace small"
              />
            </>
          ) : null}
        </ModalBody>
        <ModalFooter className="flex-wrap gap-2">
          <Button color="light" type="button" onClick={() => setBacklogPreview(null)}>
            {t("admin.supportTickets.actions.close")}
          </Button>
          {backlogPreview ? (
            <Button
              color="soft-secondary"
              type="button"
              tag="a"
              href={backlogPreview.githubUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("admin.supportTickets.summary.openGithubDraft")}
            </Button>
          ) : null}
          <Button
            color="primary"
            type="button"
            onClick={() => void handleCopyBacklogPreview()}
          >
            {t("admin.supportTickets.summary.copyRegisterTemplate")}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default SuperAdminSupportTickets;
